import { Directive, ElementRef, EventEmitter, HostBinding, Renderer2, Optional, Inject, Input, NgZone } from '@angular/core';
import { NgControl } from '@angular/forms';
import { RTL } from '@progress/kendo-angular-l10n';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { filter } from 'rxjs/operators/filter';
/**
 * Represents the [Kendo UI TextArea directive for the Inputs components for Angular]({% slug overview_textarea %}).
 * Provides floating labels to `textarea` elements.
 *
 * @example
 * ```ts-no-run
 * <textarea kendoTextArea></textarea>
 * ```
 */
export class TextAreaDirective {
    constructor(renderer, element, zone, control, rtl) {
        this.renderer = renderer;
        this.element = element;
        this.zone = zone;
        this.control = control;
        /**
         * @hidden
         */
        this.onFocus = new EventEmitter();
        /**
         * @hidden
         */
        this.onBlur = new EventEmitter();
        /**
         * @hidden
         */
        this.onValueChange = new EventEmitter();
        /**
         * Specifies if the `textarea` element will resize its height automatically
         * ([see example]({% slug overview_textarea %}#toc-auto-resizing)).
         *
         * @default false
         */
        this.autoSize = false;
        this.listeners = [];
        this.direction = rtl ? 'rtl' : 'ltr';
    }
    get elementClass() {
        return true;
    }
    /**
     * @hidden
     */
    set value(text) {
        if (!this.element) {
            return;
        }
        this.element.nativeElement.value = (text === undefined || text === null) ? '' : text;
        this.onValueChange.emit();
    }
    /**
     * @hidden
     */
    get value() {
        return this.element.nativeElement.value;
    }
    get id() {
        return this.element.nativeElement.id;
    }
    set id(id) {
        this.renderer.setAttribute(this.element.nativeElement, 'id', id);
    }
    ngOnChanges(changes) {
        const element = this.element.nativeElement;
        if (changes.autoSize) {
            if (this.autoSize) {
                this.initialHeight = element.offsetHeight;
                this.renderer.setStyle(element, 'resize', 'none');
            }
            else {
                this.renderer.setStyle(element, 'overflow-y', 'auto');
                this.renderer.setStyle(element, 'resize', 'both');
                element.style.height = `${this.initialHeight}px`;
            }
        }
        if (this.autoSize) {
            this.resize();
        }
    }
    ngOnInit() {
        const element = this.element.nativeElement;
        this.listeners = [
            this.renderer.listen(element, 'focus', () => this.onFocus.emit()),
            this.renderer.listen(element, 'blur', () => this.onBlur.emit())
        ];
        this.zone.runOutsideAngular(() => {
            this.inputSubscription = fromEvent(element, 'input')
                .pipe(filter(() => this.autoSize))
                .subscribe(() => this.resize());
        });
        if (this.control) {
            this.valueChangeSubscription = this.control.valueChanges
                .pipe(filter(() => this.autoSize))
                .subscribe(() => this.resize());
        }
    }
    ngOnDestroy() {
        this.listeners.forEach(listener => listener());
        if (this.valueChangeSubscription) {
            this.valueChangeSubscription.unsubscribe();
        }
    }
    resize() {
        const element = this.element.nativeElement;
        this.renderer.setStyle(element, 'overflow-y', 'hidden');
        element.style.height = `${this.initialHeight}px`;
        const scrollHeight = element.scrollHeight;
        if (scrollHeight > this.initialHeight) {
            element.style.height = `${scrollHeight}px`;
        }
    }
}
TextAreaDirective.decorators = [
    { type: Directive, args: [{
                selector: 'textarea[kendoTextArea]'
            },] },
];
/** @nocollapse */
TextAreaDirective.ctorParameters = () => [
    { type: Renderer2, },
    { type: ElementRef, },
    { type: NgZone, },
    { type: NgControl, decorators: [{ type: Optional },] },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [RTL,] },] },
];
TextAreaDirective.propDecorators = {
    'elementClass': [{ type: HostBinding, args: ['class.k-textarea',] },],
    'direction': [{ type: HostBinding, args: ['attr.dir',] },],
    'autoSize': [{ type: Input },],
    'value': [{ type: Input },],
};
